import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';
import { 
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  XCircleIcon,
  MapPinIcon,
  Squares2X2Icon,
  QueueListIcon,
  TagIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import DepartmentManager from '../../components/employees/DepartmentManager';
import DepartmentSynchronizer from '../../components/employees/DepartmentSynchronizer';
import DepartmentDistribution from '../../components/employees/DepartmentDistribution';
import { getDepartments, addDepartment as addDeptUtil } from '../../utils/departmentUtils';

// Main Page component with tabs
const EmployeesMainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('employees');

  // Determine active tab based on URL
  useEffect(() => {
    if (location.pathname.includes('/employees/departments')) {
      setActiveTab('departments');
    } else {
      setActiveTab('employees');
    }
  }, [location.pathname]);

  return (
    <div className="space-y-6">
      {/* Invisible utility component to handle department synchronization */}
      <DepartmentSynchronizer />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-white">Employés</h1>
          <p className="text-white/60">Gérez votre personnel et les départements</p>
        </div>
        <Link 
          to="/employees/new" 
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Nouvel employé
        </Link>
      </div>

      {/* Tab navigation */}
      <div className="flex space-x-2 mb-6">
        <Link
          to="/employees"
          className={`flex items-center px-4 py-3 rounded-xl transition-colors ${activeTab === 'employees' ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
        >
          <QueueListIcon className="h-5 w-5 mr-2" />
          Liste du personnel
        </Link>
        <Link
          to="/employees/departments"
          className={`flex items-center px-4 py-3 rounded-xl transition-colors ${activeTab === 'departments' ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
        >
          <TagIcon className="h-5 w-5 mr-2" />
          Départements
        </Link>
      </div>

      {activeTab === 'employees' && <EmployeesList />}
      {activeTab === 'departments' && <DepartmentManager />}
    </div>
  );
};

const EmployeesList = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage] = useState(6);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  // Exemple de données d'employés
  const initialEmployees = [
    {
      id: 'EMP-001',
      firstName: 'Karim',
      lastName: 'Benali',
      email: 'k.benali@example.com',
      phone: '+213 555 123 456',
      position: 'Directeur général',
      department: 'management',
      joinDate: '2020-02-10',
      status: 'active',
      salary: 180000,
      address: '15 Rue des Oliviers, Alger',
      socialSecurity: '123456789012',
      bankDetails: 'BNA 12345678900',
      emergencyContact: 'Amina Benali: +213 555 987 654',
      education: 'Master en Management, Université d\'Alger',
      certifications: ['MBA', 'PMP'],
      schedule: 'Lun-Ven: 08:00-17:00'
    },
    {
      id: 'EMP-002',
      firstName: 'Fatima',
      lastName: 'Zahra',
      email: 'f.zahra@example.com',
      phone: '+213 555 234 567',
      position: 'Responsable accueil',
      department: 'reception',
      joinDate: '2021-04-15',
      status: 'active',
      salary: 75000,
      address: '7 Avenue Mohamed V, Alger',
      socialSecurity: '234567890123',
      bankDetails: 'CPA 23456789011',
      emergencyContact: 'Mohamed Zahra: +213 555 876 543',
      education: 'Licence en Communication, Université d\'Alger',
      certifications: ['Service client'],
      schedule: 'Lun-Sam: 10:00-19:00'
    },
    {
      id: 'EMP-003',
      firstName: 'Ahmed',
      lastName: 'Rahmani',
      email: 'a.rahmani@example.com',
      phone: '+213 555 345 678',
      position: 'Coach sportif',
      department: 'coaching',
      joinDate: '2021-06-22',
      status: 'active',
      salary: 90000,
      address: '22 Boulevard des Martyrs, Oran',
      socialSecurity: '345678901234',
      bankDetails: 'BEA 34567890122',
      emergencyContact: 'Leila Rahmani: +213 555 765 432',
      education: 'Master en Sciences du Sport, Université d\'Oran',
      certifications: ['Coach certifié', 'Nutrition sportive'],
      schedule: 'Mar-Sam: 09:00-18:00'
    },
    {
      id: 'EMP-004',
      firstName: 'Amina',
      lastName: 'Belkacem',
      email: 'a.belkacem@example.com',
      phone: '+213 555 456 789',
      position: 'Comptable',
      department: 'finance',
      joinDate: '2022-01-15',
      status: 'vacation',
      salary: 95000,
      address: '8 Rue Ali Boumendjel, Constantine',
      socialSecurity: '456789012345',
      bankDetails: 'BDL 45678901233',
      emergencyContact: 'Samir Belkacem: +213 555 654 321',
      education: 'Licence en Comptabilité, Université de Constantine',
      certifications: ['Expert-comptable'],
      schedule: 'Lun-Jeu: 08:30-16:30'
    },
    {
      id: 'EMP-005',
      firstName: 'Omar',
      lastName: 'Kaddouri',
      email: 'o.kaddouri@example.com',
      phone: '+213 555 567 890',
      position: 'Technicien maintenance',
      department: 'maintenance',
      joinDate: '2021-09-05',
      status: 'active',
      salary: 70000,
      address: '33 Rue Larbi Ben M\'Hidi, Annaba',
      socialSecurity: '567890123456',
      bankDetails: 'BNA 56789012344',
      emergencyContact: 'Yasmine Kaddouri: +213 555 543 210',
      education: 'BTS en Maintenance Industrielle, INSFP Annaba',
      certifications: ['Électricité', 'Plomberie'],
      schedule: 'Lun-Ven: 07:00-15:00'
    },
    {
      id: 'EMP-006',
      firstName: 'Nadia',
      lastName: 'Hamidi',
      email: 'n.hamidi@example.com',
      phone: '+213 555 678 901',
      position: 'Agent d\'entretien',
      department: 'cleaning',
      joinDate: '2022-03-01',
      status: 'active',
      salary: 60000,
      address: '14 Rue des Frères Bouadou, Tizi Ouzou',
      socialSecurity: '678901234567',
      bankDetails: 'CPA 67890123455',
      emergencyContact: 'Ali Hamidi: +213 555 432 109',
      education: 'Baccalauréat',
      certifications: [],
      schedule: 'Lun-Sam: 06:00-14:00'
    },
    {
      id: 'EMP-007',
      firstName: 'Yacine',
      lastName: 'Bouzidi',
      email: 'y.bouzidi@example.com',
      phone: '+213 555 789 012',
      position: 'Coach sportif senior',
      department: 'coaching',
      joinDate: '2020-05-20',
      status: 'active',
      salary: 95000,
      address: '27 Avenue Didouche Mourad, Alger',
      socialSecurity: '789012345678',
      bankDetails: 'BEA 78901234566',
      emergencyContact: 'Selma Bouzidi: +213 555 321 098',
      education: 'Licence en Éducation Physique, ENS Alger',
      certifications: ['Coach certifié niveau 2', 'Musculation', 'Réadaptation sportive'],
      schedule: 'Lun-Ven: 16:00-22:00'
    }
  ];

  // Départements disponibles
  const departments = [
    { value: 'all', label: 'Tous les départements' },
    { value: 'management', label: 'Direction' },
    { value: 'reception', label: 'Accueil' },
    { value: 'coaching', label: 'Coaching' },
    { value: 'finance', label: 'Finance' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'cleaning', label: 'Nettoyage' }
  ];

  // Statuts disponibles
  const statuses = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'active', label: 'Actif' },
    { value: 'vacation', label: 'En congé' },
    { value: 'inactive', label: 'Inactif' }
  ];

  // Charger les données au chargement du composant
  useEffect(() => {
    // Dans une vraie application, cela serait un appel API
    const fetchEmployees = () => {
      setLoading(true);
      
      // Vérifier si des employés sont stockés dans localStorage
      const storedEmployees = localStorage.getItem('stockpro_employees');
      
      if (storedEmployees) {
        try {
          const parsedEmployees = JSON.parse(storedEmployees);
          // Si des données sont trouvées dans localStorage, les utiliser
          setEmployees(parsedEmployees);
          console.log('Employés chargés depuis localStorage:', parsedEmployees);
          setLoading(false);
        } catch (error) {
          console.error('Erreur lors du parsing des employés depuis localStorage:', error);
          // En cas d'erreur, utiliser les données initiales
        setEmployees(initialEmployees);
        setLoading(false);
        }
      } else {
        // Si aucune donnée n'est trouvée dans localStorage, utiliser les données initiales
        // et les sauvegarder dans localStorage pour une utilisation future
        localStorage.setItem('stockpro_employees', JSON.stringify(initialEmployees));
        setEmployees(initialEmployees);
        setLoading(false);
      }
    };
    
    fetchEmployees();
  }, []);

  // Filtrer les employés
  const filteredEmployees = employees.filter(employee => {
    const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      fullName.includes(searchTerm.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = departmentFilter === 'all' || employee.department === departmentFilter;
    const matchesStatus = statusFilter === 'all' || employee.status === statusFilter;
    
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  // Pagination
  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstEmployee, indexOfLastEmployee);
  const totalPages = Math.ceil(filteredEmployees.length / employeesPerPage);

  // Stats sur les employés
  const employeeStats = [
    {
      title: 'Total des employés',
      value: employees.length,
      icon: UsersIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Coaches',
      value: employees.filter(e => e.department === 'coaching').length,
      icon: UserIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Masse salariale',
      value: `${employees.reduce((sum, e) => sum + e.salary, 0).toLocaleString('fr-DZ')} DA`,
      icon: BanknotesIcon,
      color: 'from-amber-500 to-amber-600',
    }
  ];

  // Gérer la visualisation d'un employé
  const handleViewEmployee = (id) => {
    navigate(`/employees/${id}`);
  };

  // Gérer la modification d'un employé
  const handleEditEmployee = (id) => {
    navigate(`/employees/${id}/edit`);
  };

  // Gérer la suppression d'un employé
  const handleDeleteEmployee = (id, name) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'employé ${name} ?`)) {
      try {
        // Supprimer l'employé de l'état local
      setEmployees(prevEmployees => prevEmployees.filter(employee => employee.id !== id));
        
        // Supprimer également l'employé de localStorage
        const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
        const updatedEmployees = storedEmployees.filter(employee => employee.id !== id);
        localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
        
      toast.success('Employé supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'employé:', error);
        toast.error('Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Affichage du tableau des employés

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {employeeStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>



      {/* Barre de recherche et filtres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-grow">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Rechercher un employé par nom, poste, ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="pl-4 pr-8 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23FFFFFF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em", paddingRight: "2.5rem" }}
            >
              {departments.map((dept) => (
                <option key={dept.value} value={dept.value} className="bg-slate-800">
                  {dept.label}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-4 pr-8 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23FFFFFF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em", paddingRight: "2.5rem" }}
            >
              {statuses.map((status) => (
                <option key={status.value} value={status.value} className="bg-slate-800">
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Employees Table */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Employé
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Poste
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Département
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/5 divide-y divide-white/10">
                  {currentEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-white/10 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {employee.avatar ? (
                            <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden">
                              <img 
                                src={employee.avatar} 
                                alt={`${employee.firstName} ${employee.lastName}`}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-white/70" />
                          </div>
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-sm text-white/60">
                              {employee.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{employee.email}</div>
                        <div className="text-sm text-white/60">{employee.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{employee.position}</div>
                        <div className="text-sm text-white/60">Depuis {new Date(employee.joinDate).toLocaleDateString('fr-DZ')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {departments.find(d => d.value === employee.department)?.label || employee.department}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          employee.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : employee.status === 'vacation'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {employee.status === 'active' 
                            ? 'Actif' 
                            : employee.status === 'vacation' 
                            ? 'En congé' 
                            : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => handleViewEmployee(employee.id)}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                            title="Voir"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEditEmployee(employee.id)}
                            className="text-amber-400 hover:text-amber-300 transition-colors"
                            title="Modifier"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteEmployee(employee.id, `${employee.firstName} ${employee.lastName}`)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                            title="Supprimer"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty state */}
            {currentEmployees.length === 0 && (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-white/20" />
                <h3 className="mt-2 text-lg font-medium text-white">Aucun employé trouvé</h3>
                <p className="mt-1 text-white/60">Aucun employé ne correspond aux critères de recherche.</p>
              </div>
            )}

            {/* Pagination */}
            {filteredEmployees.length > 0 && (
              <div className="border-t border-white/10 px-6 py-4 flex items-center justify-between">
                <div className="text-sm text-white/60">
                  Affichage de {Math.min(filteredEmployees.length, indexOfFirstEmployee + 1)} à {Math.min(indexOfLastEmployee, filteredEmployees.length)} sur {filteredEmployees.length} employés
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <div className="px-4 py-2 bg-white/5 rounded-lg text-white">
                    {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Department Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 mt-6"
      >
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 mr-2 text-blue-400" />
          <h2 className="text-xl font-semibold text-white">Répartition par département</h2>
        </div>
        <DepartmentDistribution />
      </motion.div>
    </div>
  );
};

// Composant détail d'employé
const EmployeeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Rôles utilisateur (identique à EmployeeForm)
  const userRoles = [
    { value: 'user', label: 'Utilisateur standard' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Administrateur' },
    { value: 'superadmin', label: 'Super Administrateur' }
  ];
  
  // Modules du système pour les permissions (identique à EmployeeForm)
  const systemModules = [
    { id: 'dashboard', name: 'Tableau de bord', actions: ['view', 'edit'] },
    { id: 'products', name: 'Produits', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'stock', name: 'Stock', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'suppliers', name: 'Fournisseurs', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'clients', name: 'Clients', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'orders', name: 'Commandes', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'sales', name: 'Ventes', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'purchases', name: 'Achats', actions: ['view', 'edit', 'create', 'delete'] },

    { id: 'reports', name: 'Rapports', actions: ['view', 'export'] },
    { id: 'employees', name: 'Employés', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'settings', name: 'Paramètres', actions: ['view', 'edit'] }
  ];

  useEffect(() => {
    const fetchEmployeeData = () => {
      setLoading(true);
      
      // Récupérer les employés depuis localStorage
      const storedEmployees = localStorage.getItem('stockpro_employees');
      
      if (storedEmployees) {
        try {
          const parsedEmployees = JSON.parse(storedEmployees);
          const foundEmployee = parsedEmployees.find(emp => emp.id === id);
          
          if (foundEmployee) {
            // Si l'employé est trouvé dans localStorage
            console.log('Employé trouvé dans localStorage:', foundEmployee);
            setEmployee(foundEmployee);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données employé:', error);
        }
      }
      
      // Employé non trouvé, rediriger vers la liste des employés avec un message d'erreur
      toast.error(`L'employé avec l'ID ${id} n'a pas été trouvé.`);
      console.warn(`Employé avec ID ${id} non trouvé dans localStorage.`);
      navigate('/employees');
      setLoading(false);
    };
    
    fetchEmployeeData();
  }, [id]);



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/employees')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <h1 className="text-3xl font-bold text-white">
            {loading ? 'Chargement...' : `${employee?.firstName} ${employee?.lastName}`}
          </h1>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/employees/${id}/edit`)}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors flex items-center space-x-2"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Modifier</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 flex justify-center">
          <p className="text-white">Chargement des informations...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne d'informations principales */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informations personnelles */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Informations personnelles</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-white/60 text-sm">Nom complet</p>
                  <p className="text-white text-lg font-medium">{employee.firstName} {employee.lastName}</p>
                </div>
                
                <div>
                  <p className="text-white/60 text-sm">Email</p>
                  <p className="text-white text-lg">{employee.email}</p>
                </div>
                
                <div>
                  <p className="text-white/60 text-sm">Téléphone</p>
                  <p className="text-white text-lg">{employee.phone}</p>
                </div>
                
                <div>
                  <p className="text-white/60 text-sm">Poste</p>
                  <p className="text-white text-lg">{employee.position}</p>
                </div>
                
                <div>
                  <p className="text-white/60 text-sm">Département</p>
                  <p className="text-white text-lg">{employee.department}</p>
                </div>
                
                <div>
                  <p className="text-white/60 text-sm">Date d'embauche</p>
                  <p className="text-white text-lg">{new Date(employee.joinDate).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            </div>
            

            
            {/* Notes */}
            {employee.notes && (
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <h2 className="text-xl font-semibold text-white mb-4">Notes</h2>
                <p className="text-white/80">{employee.notes}</p>
              </div>
            )}
          </div>
          
          {/* Colonne latérale */}
          <div className="space-y-6">
            {/* Photo */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Photo</h2>
              
              <div className="flex flex-col items-center justify-center">
                {employee.avatar ? (
                  <img 
                    src={employee.avatar} 
                    alt={`${employee.firstName} ${employee.lastName}`}
                    className="w-48 h-48 object-cover rounded-xl" 
                  />
                ) : (
                  <div className="w-48 h-48 bg-white/5 border border-white/20 rounded-xl flex flex-col items-center justify-center">
                    <UserIcon className="h-16 w-16 text-white/30" />
                    <p className="text-white/50 text-sm mt-2">Aucune photo</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Statut */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Statut</h2>
              
              <div className="flex items-center space-x-3">
                <div className={`h-3 w-3 rounded-full ${
                  employee.status === 'active' ? 'bg-green-500' :
                  employee.status === 'vacation' ? 'bg-blue-500' :
                  employee.status === 'probation' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}></div>
                <span className="text-white capitalize">
                  {employee.status === 'active' ? 'Actif' :
                   employee.status === 'vacation' ? 'En congé' :
                   employee.status === 'probation' ? 'Période d\'essai' :
                   'Inactif'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant formulaire d'employé
const EmployeeForm = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [showNewDepartmentInput, setShowNewDepartmentInput] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Get departments and functions from context
  const { departments: contextDepartments, addDepartment } = useAppData() || {};
  
  // Debug log departments and set up storage event listener
  useEffect(() => {
    console.log('Employee form - departments from context:', contextDepartments);
    // Also check localStorage directly
    try {
      const storedDepts = localStorage.getItem('stockpro_departments');
      if (storedDepts) {
        console.log('Employee form - departments from localStorage:', JSON.parse(storedDepts));
      }
    } catch (error) {
      console.error('Error reading departments in employee form:', error);
    }
    
    // Listen for storage events to refresh department data
    const handleStorageChange = (event) => {
      if (event.key === 'stockpro_departments' || !event.key) {
        console.log('Storage event detected in employee form, refreshing departments');
        setRefreshTrigger(prev => prev + 1);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, [contextDepartments]);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    department: 'stock',
    joinDate: new Date().toISOString().split('T')[0],
    status: 'active',
    isResponsible: false,
    isTransporter: false,
    transportZones: [],
    warehouseAccess: [],
    permissionLevel: 'standard',
    role: 'user',
    permissions: {
      dashboard: { view: true, edit: false },
      products: { view: true, edit: false, create: false, delete: false },
      stock: { view: true, edit: false, create: false, delete: false },
      suppliers: { view: true, edit: false, create: false, delete: false },
      clients: { view: true, edit: false, create: false, delete: false },
      orders: { view: true, edit: false, create: false, delete: false },
      sales: { view: true, edit: false, create: false, delete: false },
      purchases: { view: true, edit: false, create: false, delete: false },
      
      reports: { view: true, export: false },
      employees: { view: false, edit: false, create: false, delete: false },
      settings: { view: false, edit: false }
    },
    notes: '',
    avatar: null,
    avatarPreview: null
  });

  // Map des départements standards pour les libellés
  const departmentLabels = {
    'Stock': 'Stock',
    'Logistique': 'Logistique',
    'Achats': 'Achats',
    'Ventes': 'Ventes',
    'Transport': 'Transport',
    'Direction': 'Direction',
    'Informatique': 'Informatique',
    'Comptabilité': 'Comptabilité',
    'Accueil': 'Accueil',
    'Coaching': 'Coaching'
  };
  
  // Chargement des départements depuis localStorage pour le dropdown
  const departmentOptions = useMemo(() => {
    // Charger les départements avec l'utilitaire centralisé
    const storedDepts = getDepartments();
    console.log("Form loaded departments for dropdown:", storedDepts);
    
    // Convertir en format {value, label} pour le dropdown
    const options = [
      { value: '', label: 'Sélectionner un département' },
      ...storedDepts.map(dept => ({
        value: dept,
        label: departmentLabels[dept] || dept // Utiliser le label standard ou le nom brut
      })),
      { value: 'new', label: '+ Ajouter un nouveau département' }
    ];
    
    return options;
  }, [refreshTrigger]);

  // Liste des statuts
  const statuses = [
    { value: 'active', label: 'Actif' },
    { value: 'vacation', label: 'En congé' },
    { value: 'inactive', label: 'Inactif' },
    { value: 'probation', label: 'Période d\'essai' }
  ];
  
  // Niveaux de permission
  const permissionLevels = [
    { value: 'standard', label: 'Standard' },
    { value: 'advanced', label: 'Avancé' },
    { value: 'admin', label: 'Administrateur' },
    { value: 'superadmin', label: 'Super Administrateur' }
  ];
  
  // Rôles utilisateur
  const userRoles = [
    { value: 'user', label: 'Utilisateur standard' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Administrateur' },
    { value: 'superadmin', label: 'Super Administrateur' }
  ];
  
  // Modules du système pour les permissions
  const systemModules = [
    { id: 'dashboard', name: 'Tableau de bord', actions: ['view', 'edit'] },
    { id: 'products', name: 'Produits', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'stock', name: 'Stock', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'suppliers', name: 'Fournisseurs', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'clients', name: 'Clients', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'orders', name: 'Commandes', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'sales', name: 'Ventes', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'purchases', name: 'Achats', actions: ['view', 'edit', 'create', 'delete'] },

    { id: 'reports', name: 'Rapports', actions: ['view', 'export'] },
    { id: 'employees', name: 'Employés', actions: ['view', 'edit', 'create', 'delete'] },
    { id: 'settings', name: 'Paramètres', actions: ['view', 'edit'] }
  ];
  
  // Zones de transport simulées
  const transportZones = [
    { id: 'zone1', name: 'Zone Nord (Alger, Blida, Tipaza)' },
    { id: 'zone2', name: 'Zone Est (Constantine, Annaba)' },
    { id: 'zone3', name: 'Zone Ouest (Oran, Tlemcen)' },
    { id: 'zone4', name: 'Zone Centre (Médéa, Bouira)' },
    { id: 'zone5', name: 'Zone Sud (Ghardaïa, Ouargla)' }
  ];

  // Récupérer les entrepôts disponibles
  const { getWarehouses } = useAppData();
  
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        // Essayer d'abord de récupérer depuis le contexte
        const warehousesData = getWarehouses ? getWarehouses() : [];
        
        if (warehousesData && warehousesData.length > 0) {
          console.log('Entrepôts chargés depuis le contexte:', warehousesData.length);
          setWarehouses(warehousesData);
          return;
        }
        
        // Si aucun entrepôt n'est trouvé dans le contexte, essayer de charger depuis localStorage
        const storedWarehouses = localStorage.getItem('stockpro_warehouses');
        if (storedWarehouses) {
          try {
            const parsedWarehouses = JSON.parse(storedWarehouses);
            console.log('Entrepôts chargés depuis localStorage:', parsedWarehouses.length);
            setWarehouses(parsedWarehouses);
            return;
          } catch (parseError) {
            console.error('Erreur lors du parsing des entrepôts depuis localStorage:', parseError);
          }
        }
        
        // Si aucune donnée n'est disponible, utiliser un tableau vide
        console.log('Aucun entrepôt trouvé, utilisation d\'un tableau vide');
        setWarehouses([]);
      } catch (error) {
        console.error('Erreur lors de la récupération des entrepôts:', error);
        toast.error('Impossible de charger la liste des entrepôts');
        setWarehouses([]);
      }
    };
    
    fetchWarehouses();
  }, [getWarehouses]);

  // Add a storage event listener to refresh departments when they change
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'stockpro_departments' || !e.key) {
        console.log('EmployeeForm detected department changes, refreshing dropdown');
        setRefreshTrigger(prev => prev + 1);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Charger les données au chargement en mode édition
  useEffect(() => {
    if (isEditMode) {
      setIsLoading(true);
      
      // Récupérer les employés depuis localStorage
      const storedEmployees = localStorage.getItem('stockpro_employees');
      
      if (storedEmployees) {
        try {
          const parsedEmployees = JSON.parse(storedEmployees);
          const foundEmployee = parsedEmployees.find(emp => emp.id === id);
          
          if (foundEmployee) {
            console.log('Employé trouvé pour édition:', foundEmployee);
            
            // S'assurer que l'employé a un objet permissions complet
            const permissions = foundEmployee.permissions || {
              dashboard: { view: true, edit: false },
              products: { view: true, edit: false, create: false, delete: false },
              stock: { view: true, edit: false, create: false, delete: false },
              suppliers: { view: true, edit: false, create: false, delete: false },
              clients: { view: true, edit: false, create: false, delete: false },
              orders: { view: true, edit: false, create: false, delete: false },
              sales: { view: true, edit: false, create: false, delete: false },
              purchases: { view: true, edit: false, create: false, delete: false },
              reports: { view: true, export: false },
              employees: { view: false, edit: false, create: false, delete: false },
              settings: { view: false, edit: false }
            };
            
            // Mettre à jour le formulaire avec les données de l'employé
            setFormData({
              ...foundEmployee,
              permissions: permissions,
              avatar: null,
              avatarPreview: foundEmployee.avatar || null
            });
            
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données employé:', error);
        }
      }
      
      // Employé non trouvé, rediriger vers la liste des employés avec un message d'erreur
      toast.error(`L'employé avec l'ID ${id} n'a pas été trouvé pour édition.`);
      console.warn(`Employé avec ID ${id} non trouvé pour édition.`);
      navigate('/employees');
      setIsLoading(false);
    }
  }, [isEditMode, id]);

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'isResponsible' || name === 'isTransporter') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else if (name.startsWith('warehouse-')) {
        const warehouseId = name.replace('warehouse-', '');
        setFormData(prev => {
          const currentAccess = [...prev.warehouseAccess];
          
          if (checked) {
            if (!currentAccess.includes(warehouseId)) {
              currentAccess.push(warehouseId);
            }
          } else {
            const index = currentAccess.indexOf(warehouseId);
            if (index !== -1) {
              currentAccess.splice(index, 1);
            }
          }
          
          return {
            ...prev,
            warehouseAccess: currentAccess
          };
        });
      } else if (name.startsWith('zone-')) {
        const zoneId = name.replace('zone-', '');
        setFormData(prev => {
          const currentZones = [...prev.transportZones];
          
          if (checked) {
            if (!currentZones.includes(zoneId)) {
              currentZones.push(zoneId);
            }
          } else {
            const index = currentZones.indexOf(zoneId);
            if (index !== -1) {
              currentZones.splice(index, 1);
            }
          }
          
          return {
            ...prev,
            transportZones: currentZones
          };
        });
      }
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
    }
  };
  
  // Gérer l'upload de la photo
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type et la taille du fichier
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    if (!validTypes.includes(file.type)) {
      toast.error('Format d\'image non supporté. Utilisez JPG, PNG ou WebP.');
      return;
    }
    
    if (file.size > maxSize) {
      toast.error('Image trop volumineuse. La taille maximale est de 2MB.');
      return;
    }
    
    // Prévisualiser l'image
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        avatar: file,
        avatarPreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeAvatar = () => {
    setFormData(prev => ({
      ...prev,
      avatar: null,
      avatarPreview: null
    }));
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Simuler un délai de traitement
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Préparer les données de l'employé
      let employeeData = {
        ...formData,
        id: isEditMode ? id : `EMP-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`
      };

      // Gérer l'image de l'employé
      if (formData.avatarPreview) {
        // Stocker directement la chaîne base64 de l'image
        employeeData.avatar = formData.avatarPreview;
      } else {
        employeeData.avatar = null;
      }

      console.log("Données de l'employé:", employeeData);
      
      // Ajouter l'employé à la liste des employés dans localStorage
      const existingEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
      
      if (isEditMode) {
        // Mise à jour d'un employé existant
        const updatedEmployees = existingEmployees.map(emp => 
          emp.id === employeeData.id ? employeeData : emp
        );
        localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
      } else {
        // Ajout d'un nouvel employé
        existingEmployees.push(employeeData);
        localStorage.setItem('stockpro_employees', JSON.stringify(existingEmployees));
      }
      
      // Afficher un toast de succès
      toast.success(isEditMode 
        ? `L'employé ${formData.firstName} ${formData.lastName} a été mis à jour` 
        : `L'employé ${formData.firstName} ${formData.lastName} a été ajouté avec succès`);
      
      // Rediriger vers la liste des employés
      navigate('/employees');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/employees')}
          className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-6 w-6 text-white" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">
            {isEditMode ? 'Modifier l\'employé' : 'Ajouter un employé'}
          </h1>
          <p className="text-white/60 mt-1">
            {isEditMode ? 'Modifier les informations de l\'employé' : 'Ajouter un nouvel employé au système d\'inventaire'}
          </p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Colonne principale - Informations de l'employé */}
              <div className="lg:col-span-2 space-y-6">
                {/* Informations personnelles */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Informations personnelles</h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-white font-medium mb-1">
                          Prénom <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Prénom"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="lastName" className="block text-white font-medium mb-1">
                          Nom <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Nom"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-white font-medium mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="email@exemple.com"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="phone" className="block text-white font-medium mb-1">
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="+213 XXX XXX XXX"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Informations professionnelles */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Informations professionnelles</h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="position" className="block text-white font-medium mb-1">
                          Poste <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="position"
                          name="position"
                          value={formData.position}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Intitulé du poste"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="department" className="block text-white font-medium mb-1">
                          Département
                        </label>
                        {!showNewDepartmentInput ? (
                          <>
                        <select
                          id="department"
                          name="department"
                          value={formData.department}
                              onChange={(e) => {
                                if (e.target.value === "new") {
                                  setShowNewDepartmentInput(true);
                                } else {
                                  handleChange(e);
                                }
                              }}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                              {departmentOptions.map((option, index) => (
                                <option 
                                  key={`${option.value}-${index}`} 
                                  value={option.value} 
                                  className={option.value === 'new' ? "bg-green-800" : "bg-slate-800"}
                                >
                                  {option.label}
                            </option>
                          ))}
                        </select>
                          </>
                        ) : (
                          <div className="flex space-x-2">
                            <input
                              type="text"
                              placeholder="Nom du nouveau département"
                              value={newDepartmentName}
                              onChange={(e) => setNewDepartmentName(e.target.value)}
                              className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (newDepartmentName.trim()) {
                                  const newDeptValue = newDepartmentName.trim().toLowerCase().replace(/\s+/g, '_');
                                  
                                  // Add department using our centralized utility
                                  const result = addDeptUtil(newDeptValue);
                                  if (result) {
                                    console.log('Added new department from form:', newDeptValue);
                                    // Force refresh to update dropdown
                                    setRefreshTrigger(prev => prev + 1);
                                  } else {
                                    console.error('Failed to add department from form');
                                  }
                                  
                                  // Update form data with new department
                                  setFormData(prev => ({
                                    ...prev,
                                    department: newDeptValue
                                  }));
                                  
                                  // Reset the input fields
                                  setNewDepartmentName('');
                                  setShowNewDepartmentInput(false);
                                  toast.success('Département ajouté avec succès');
                                } else {
                                  toast.error('Veuillez saisir un nom de département');
                                }
                              }}
                              className="p-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setNewDepartmentName('');
                                setShowNewDepartmentInput(false);
                              }}
                              className="p-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="joinDate" className="block text-white font-medium mb-1">
                          Date d'embauche
                        </label>
                        <input
                          type="date"
                          id="joinDate"
                          name="joinDate"
                          value={formData.joinDate}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="status" className="block text-white font-medium mb-1">
                          Statut
                        </label>
                        <select
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {statuses.map(status => (
                            <option key={status.value} value={status.value} className="bg-slate-800">
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="isResponsible"
                          name="isResponsible"
                          checked={formData.isResponsible}
                          onChange={handleChange}
                          className="h-5 w-5 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="isResponsible" className="text-white font-medium">
                          Responsable (privilèges additionnels)
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="isTransporter"
                          name="isTransporter"
                          checked={formData.isTransporter}
                          onChange={handleChange}
                          className="h-5 w-5 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="isTransporter" className="text-white font-medium">
                          Transporteur
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Zones de transport (apparaît si l'employé est transporteur) */}
                {formData.isTransporter && (
                  <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                    <h2 className="text-xl font-semibold text-white mb-6">Zones de transport</h2>
                    <div className="space-y-3">
                      <p className="text-white/80 mb-4">Sélectionnez les zones dans lesquelles cet employé peut effectuer des livraisons</p>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {transportZones.map(zone => (
                          <div key={zone.id} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`zone-${zone.id}`}
                              name={`zone-${zone.id}`}
                              checked={formData.transportZones.includes(zone.id)}
                              onChange={handleChange}
                              className="h-5 w-5 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-blue-500 mr-3"
                            />
                            <label htmlFor={`zone-${zone.id}`} className="text-white flex items-center">
                              <MapPinIcon className="h-5 w-5 mr-2 text-white/70" />
                              {zone.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Accès aux entrepôts */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Accès aux entrepôts</h2>
                  
                  <div className="space-y-3">
                    {warehouses.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {warehouses.map(warehouse => (
                          <div key={warehouse.id} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`warehouse-${warehouse.id}`}
                              name={`warehouse-${warehouse.id}`}
                              checked={formData.warehouseAccess.includes(warehouse.id)}
                              onChange={handleChange}
                              className="h-5 w-5 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-blue-500 mr-2"
                            />
                            <label htmlFor={`warehouse-${warehouse.id}`} className="text-white">
                              {warehouse.name}
                              <span className="text-xs text-white/60 ml-2">({warehouse.location})</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center p-4 border border-dashed border-white/20 rounded-xl">
                        <p className="text-white/60">Chargement des entrepôts...</p>
                      </div>
                    )}
                  </div>
                </div>
                

                
                {/* Notes */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Notes</h2>
                  
                  <div>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Notes ou informations supplémentaires sur cet employé..."
                      rows="4"
                    />
                  </div>
                </div>
              </div>
              
              {/* Colonne latérale - Photo et résumé */}
              <div className="space-y-6">
                {/* Photo de l'employé */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Photo</h2>
                  
                  <div className="flex flex-col items-center justify-center">
                    {formData.avatarPreview ? (
                      <div className="relative w-48 h-48 mb-4">
                        <img 
                          src={formData.avatarPreview} 
                          alt="Photo prévisualisée"
                          className="w-full h-full object-cover rounded-xl" 
                        />
                        <button
                          type="button"
                          onClick={removeAvatar}
                          className="absolute top-2 right-2 p-1 bg-red-500/80 text-white rounded-full hover:bg-red-600/80 transition-colors"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-48 h-48 bg-white/5 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center mb-4">
                        <UserIcon className="h-16 w-16 text-white/30" />
                        <p className="text-white/50 text-sm mt-2">Aucune photo</p>
                      </div>
                    )}
                    
                    <label className="cursor-pointer px-4 py-2 bg-white/10 hover:bg-white/15 transition-colors rounded-lg text-white text-sm font-medium">
                      <span>Sélectionner une photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-white/50 text-xs mt-2">Format recommandé: PNG, JPG (max 2 MB)</p>
                  </div>
                </div>
                
                {/* Résumé */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Résumé</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <UserIcon className="h-5 w-5 text-white/70" />
                      <span className="text-white">{formData.firstName && formData.lastName ? `${formData.firstName} ${formData.lastName}` : 'Non spécifié'}</span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <BriefcaseIcon className="h-5 w-5 text-white/70" />
                      <span className="text-white">{formData.position || 'Non spécifié'}</span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="h-5 w-5 text-white/70" />
                      <span className="text-white">{formData.phone || 'Non spécifié'}</span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <EnvelopeIcon className="h-5 w-5 text-white/70" />
                      <span className="text-white">{formData.email || 'Non spécifié'}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        formData.status === 'active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : formData.status === 'vacation'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {statuses.find(s => s.value === formData.status)?.label || 'Inconnu'}
                      </span>
                      
                      {formData.isResponsible && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
                          Responsable
                        </span>
                      )}
                      
                      {formData.isTransporter && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400">
                          Transporteur
                        </span>
                      )}
                      

                    </div>
                    
                    {formData.warehouseAccess.length > 0 && (
                      <div className="mt-2">
                        <p className="text-white/70 text-sm mb-1">Entrepôts accessibles:</p>
                        <div className="flex flex-wrap gap-2">
                          {formData.warehouseAccess.map(warehouseId => {
                            const warehouse = warehouses.find(w => w.id === warehouseId);
                            return warehouse ? (
                              <span key={warehouseId} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-500/20 text-gray-300">
                                {warehouse.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {formData.isTransporter && formData.transportZones.length > 0 && (
                  <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                    <h2 className="text-xl font-semibold text-white mb-6">Zones de transport</h2>
                    
                    <div className="space-y-2">
                      {formData.transportZones.map(zoneId => {
                        const zone = transportZones.find(z => z.id === zoneId);
                        return zone ? (
                          <div key={zoneId} className="flex items-center space-x-2">
                            <MapPinIcon className="h-5 w-5 text-white/70" />
                            <span className="text-white">{zone.name}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/employees')}
                className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-white rounded-full"></span>
                    Traitement...
                  </>
                ) : isEditMode ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Router pour les différentes vues
const EmployeesRouter = () => {
  return (
    <Routes>
      <Route index element={<EmployeesMainPage />} />
      <Route path="departments" element={<EmployeesMainPage />} />
      <Route path=":id" element={<EmployeeDetail />} />
      <Route path="new" element={<EmployeeForm />} />
      <Route path=":id/edit" element={<EmployeeForm />} />
    </Routes>
  );
};

export default EmployeesRouter; 